export interface OrderPricingOption {
    title?: string;
    totalSteps: number;
    price: number;
    originalPrice?: number;
    discountPercent?: number;
    discountedPrice?: number;
}

export interface OrderDiscountTier {
    title?: string;
    minTotalDeposited: number;
    discountPercent: number;
}

export const getActiveDiscountTierTitle = (
    totalDeposited: number,
    tiers: OrderDiscountTier[],
): string | undefined => tiers
    .filter((tier) => tier.minTotalDeposited <= totalDeposited && tier.discountPercent > 0)
    .sort((first, second) => second.minTotalDeposited - first.minTotalDeposited)[0]?.title;

export interface ParsedOrderInput {
    code: string;
    totalSteps: number;
    hasValidFormat: boolean;
}

export interface OrderPriceEstimate {
    orderCount: number;
    originalTotal: number;
    payableTotal: number;
}

export const parseBulkOrderInput = (orderCode: string, defaultSteps: number): ParsedOrderInput[] => {
    const lines = orderCode
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    return lines.map((line) => {
        const parts = line.split('|').map((part) => part.trim());
        const rawCode = parts[0] || '';
        const invitedCode = rawCode.match(/--(.*?)--/);
        const code = (invitedCode?.[1] || rawCode).trim();
        const totalSteps = parts.length === 1
            ? (lines.length === 1 ? defaultSteps : 1)
            : Number(parts[1]);

        return {
            code,
            totalSteps,
            hasValidFormat: parts.length <= 2
                && /^[A-Za-z0-9]{10}$/.test(code)
                && Number.isInteger(totalSteps)
                && totalSteps > 0,
        };
    });
};

export const getCouponValidationSteps = (orderCode: string, defaultSteps: number): number => {
    const orders = parseBulkOrderInput(orderCode, defaultSteps)
        .filter((order) => order.hasValidFormat && order.code.length === 10);
    return orders.length > 0
        ? Math.min(...orders.map((order) => order.totalSteps))
        : defaultSteps;
};

export const estimateBulkOrderPrice = (
    orderCode: string,
    defaultSteps: number,
    pricing: OrderPricingOption[],
    coupon?: { discountPercent: number; maxSteps: number } | null,
): OrderPriceEstimate | undefined => {
    const orders = parseBulkOrderInput(orderCode, defaultSteps);
    if (
        orders.length === 0
        || orders.some((order) => !order.hasValidFormat || order.code.length !== 10)
        || pricing.length === 0
    ) {
        return undefined;
    }

    const singleStepPricing = pricing.find((item) => item.totalSteps === 1);
    let originalTotal = 0;
    let payableTotal = 0;
    let couponApplied = false;

    for (const order of orders) {
        const packagePricing = pricing
            .filter((item) => item.totalSteps > 1 && item.totalSteps <= order.totalSteps)
            .sort((first, second) => second.totalSteps - first.totalSteps)[0];
        const exactPricing = pricing.find((item) => item.totalSteps === order.totalSteps);
        const priceOption = packagePricing || exactPricing || singleStepPricing;
        if (!priceOption) return undefined;

        const usesPackagePrice = Boolean(packagePricing || exactPricing);
        const originalPrice = usesPackagePrice
            ? Math.round(((priceOption.originalPrice ?? priceOption.price) / priceOption.totalSteps) * order.totalSteps)
            : (priceOption.originalPrice ?? priceOption.price) * order.totalSteps;
        const vipDiscountedPrice = usesPackagePrice
            ? Math.round(((priceOption.discountedPrice ?? priceOption.price) / priceOption.totalSteps) * order.totalSteps)
            : Math.max(0, originalPrice - Math.round((originalPrice * (priceOption.discountPercent ?? 0)) / 100));
        const canApplyCoupon = coupon && !couponApplied && order.totalSteps <= coupon.maxSteps;
        const payablePrice = canApplyCoupon
            ? Math.max(0, vipDiscountedPrice - Math.round((vipDiscountedPrice * coupon.discountPercent) / 100))
            : vipDiscountedPrice;
        if (canApplyCoupon) couponApplied = true;

        originalTotal += originalPrice;
        payableTotal += payablePrice;
    }

    return { orderCount: orders.length, originalTotal, payableTotal };
};
